// Card operations against the server, with optimistic local dispositions (rollback on failure)
// and Undo. External mutations (approvals) are never optimistic: the card changes only when the
// server answers. Undo of a send or a booking is offered only inside the server's `undoUntil`
// window; a reversible Gmail change (archive, labels) is undone by cancelling it while it waits,
// else by its reverse change.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ActionResult,
  CardSummary,
  CardView,
  ComposeRemindDays,
  ThreadActionRequest,
  WorkView,
} from "@shared/api";
import type { CardAction } from "@shared/core";
import { requestWords } from "@web/inbox/labels";
import { isApiError } from "@web/lib/api";
import { feedApi, type WorkRecovery } from "@web/lib/feedApi";
import { inboxApi } from "@web/lib/inboxApi";
import { unstickApi } from "@web/lib/unstickApi";
import { invalidateThreadWork } from "@web/lib/inboxQueries";
import {
  acceptCard,
  feedKeys,
  findBoardCard,
  invalidateBoards,
  patchBoardCard,
} from "@web/lib/feedQueries";
import { formatShortDateTime } from "@web/lib/time";
import { registerUndo, undoWindowMs } from "@web/lib/undoRegistry";
import { undoReversible } from "@web/lib/undoReversible";
import { offerTeach } from "@web/teach/teachStore";
import { invalidateScheduled, sendZoneFrom } from "@web/sendLater/sendLaterApi";
import { sendLaterStore } from "@web/sendLater/sendLaterStore";
import { sendAtProblem, sendAtWords } from "@web/sendLater/when";
import { actionVerb } from "./actions";

export type CardOutcome = "decided" | "snoozed" | "drafted";

function short(title: string): string {
  return title.length > 48 ? `${title.slice(0, 45)}…` : title;
}

/** Apply a local change to the board and card caches; returns a rollback. */
function optimistic(
  queryClient: QueryClient,
  cardId: string,
  patch: (card: CardSummary) => CardSummary,
): () => void {
  const before = findBoardCard(queryClient, cardId)?.card ?? null;
  const beforeFull = queryClient.getQueryData<CardView>(feedKeys.card(cardId));
  patchBoardCard(queryClient, cardId, patch);
  if (beforeFull)
    queryClient.setQueryData(feedKeys.card(cardId), { ...beforeFull, ...patch(beforeFull) });
  return () => {
    if (before) patchBoardCard(queryClient, cardId, () => before);
    if (beforeFull) queryClient.setQueryData(feedKeys.card(cardId), beforeFull);
  };
}

function settle(queryClient: QueryClient, cardId: string) {
  invalidateBoards(queryClient);
  void queryClient.invalidateQueries({ queryKey: feedKeys.card(cardId), exact: true });
}

export async function restoreCard(queryClient: QueryClient, cardId: string): Promise<void> {
  const rollback = optimistic(queryClient, cardId, (c) => ({
    ...c,
    status: "to_review_updated",
    disposition: null,
    snoozedUntil: null,
    completedAt: null,
  }));
  try {
    acceptCard(queryClient, await feedApi.restore(cardId));
  } catch (error) {
    rollback();
    throw error;
  } finally {
    settle(queryClient, cardId);
  }
}

/** `reason` (U5 "Let it go") is kept with the dismissal on the server. */
export async function dismissCard(
  queryClient: QueryClient,
  card: CardSummary,
  reason?: string,
): Promise<boolean> {
  const rollback = optimistic(queryClient, card.id, (c) => ({
    ...c,
    status: "done",
    disposition: "dismissed",
    completedAt: new Date().toISOString(),
  }));
  try {
    acceptCard(
      queryClient,
      await (reason ? unstickApi.dismiss(card.id, reason) : feedApi.dismiss(card.id)),
    );
    registerUndo({
      message: `${reason ? "Let go" : "Dismissed"} · ${short(card.title)}`,
      undo: () => restoreCard(queryClient, card.id),
      doneMessage: "Back in this round.",
    });
    // U7: just this once, or every time? (defaults to just this once)
    offerTeach({
      threadKey: card.threadKey,
      surface: "dismiss",
      title: card.title,
      cardId: card.id,
    });
    return true;
  } catch {
    rollback();
    toast("Couldn't dismiss that card. It's still in this round.");
    return false;
  } finally {
    settle(queryClient, card.id);
  }
}

export async function snoozeCard(
  queryClient: QueryClient,
  card: CardSummary,
  until: string,
  label: string,
): Promise<boolean> {
  const rollback = optimistic(queryClient, card.id, (c) => ({
    ...c,
    status: "done",
    disposition: "snoozed",
    completedAt: new Date().toISOString(),
  }));
  try {
    const next = await feedApi.snooze(card.id, until);
    acceptCard(queryClient, next);
    const when = next.snoozedUntil ? formatShortDateTime(next.snoozedUntil) : label.toLowerCase();
    registerUndo({
      message: `Snoozed until ${when}`,
      undo: () => restoreCard(queryClient, card.id),
      doneMessage: "Back in this round.",
    });
    return true;
  } catch (error) {
    rollback();
    toast(
      isApiError(error, "invalid_request")
        ? "That time didn't make sense to MeeMail. Try “tomorrow 9am”."
        : "Couldn't snooze that card. It's still in this round.",
    );
    return false;
  } finally {
    settle(queryClient, card.id);
  }
}

export async function cancelWork(queryClient: QueryClient, workId: string): Promise<WorkView> {
  try {
    return await feedApi.workAction(workId, "cancel");
  } finally {
    invalidateBoards(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["work"] });
    void queryClient.invalidateQueries({ queryKey: ["card"] });
  }
}

export interface RunActionOptions {
  blockValues?: Record<string, string>;
  params?: { until?: string; title?: string };
  /** A send's "remind me if they don't reply" choice (0: none). */
  remindDays?: ComposeRemindDays | undefined;
}

/** What the action did, for focus-mode tallies; null when nothing happened. */
export async function runCardAction(
  queryClient: QueryClient,
  card: CardView,
  action: CardAction,
  options: RunActionOptions = {},
): Promise<CardOutcome | null> {
  const verb = actionVerb(action);
  // U12: the strip's "When it goes" for a send (null: right after the undo window).
  const sendAt = isSendKind(action) ? sendLaterStore.get(card.id) : null;
  const sendAtIssue = sendAtProblem(sendAt, Date.now());
  if (sendAtIssue) {
    toast(sendAtIssue, { description: "Nothing was sent." });
    return null;
  }
  let result: ActionResult;
  try {
    result = await feedApi.action(card.id, action.id, {
      cardRev: card.rev,
      ...(options.blockValues && Object.keys(options.blockValues).length
        ? { blockValues: options.blockValues }
        : {}),
      ...(options.params ? { params: options.params } : {}),
      ...(options.remindDays !== undefined ? { remindDays: options.remindDays } : {}),
      ...(sendAt ? { sendAt } : {}),
    });
  } catch (error) {
    if (isApiError(error, "not_available")) {
      toast(
        action.behavior === "create_task"
          ? "Tasks aren't wired up yet"
          : action.behavior === "queue_instruction"
            ? "Claude isn't wired up yet"
            : verb.notWired,
        { description: "Nothing was changed." },
      );
    } else if (isApiError(error, "stale") || isApiError(error, "conflict")) {
      toast("This card changed while you were looking. Here's the latest version.");
      void queryClient.invalidateQueries({ queryKey: feedKeys.card(card.id), exact: true });
    } else {
      toast("Couldn't do that just now. Nothing was changed.");
    }
    return null;
  } finally {
    invalidateBoards(queryClient);
  }

  acceptCard(queryClient, result.card);
  void queryClient.invalidateQueries({ queryKey: ["work"] });
  switch (result.kind) {
    case "local":
      return "decided";
    case "queued": {
      const workId = result.work.id;
      registerUndo({
        message: `Handed to Claude: ${action.label}`,
        undo: () => cancelWork(queryClient, workId),
        doneMessage: "Cancelled. The card is back for review.",
        tooLateMessage: "Too late to cancel: Claude already started.",
      });
      return "drafted";
    }
    case "approval": {
      const workId = result.work.id;
      if (result.scheduledFor) {
        scheduledUndo(queryClient, card, workId, result.scheduledFor);
        return "decided";
      }
      const window = undoWindowMs(result.undoUntil, Date.now());
      const { reverse } = result;
      const threadKey = card.threadKey;
      if (reverse && threadKey) {
        registerUndo({
          message: `${verb.doing}… ${short(card.title)}`,
          undo: () =>
            undoReversible({
              cancel: () => cancelWork(queryClient, workId),
              reverse: () => putBack(queryClient, card.id, threadKey, reverse),
            }),
          doneMessage: (how) =>
            how === "cancelled"
              ? "Cancelled. Nothing changed in Gmail."
              : `${requestWords(reverse).doing}… ${short(card.title)}`,
          tooLateMessage: "The thread changed in the meantime, so nothing was put back.",
        });
      } else if (window) {
        registerUndo({
          message: `${verb.doing}… ${short(card.title)}`,
          durationMs: window,
          undo: () => cancelWork(queryClient, workId),
          doneMessage:
            verb.doing === "Sending" || verb.doing === "Forwarding"
              ? "Cancelled. Nothing was sent."
              : verb.doing === "Booking"
                ? "Cancelled. Nothing was booked and no invitation went out."
                : verb.doing === "Holding"
                  ? "Cancelled. Nothing was held."
                  : "Cancelled. Nothing changed in Gmail.",
          tooLateMessage: "Too late to undo: it's already on its way.",
        });
      } else {
        toast(`${verb.doing}… ${short(card.title)}`);
      }
      return "decided";
    }
  }
}

function isSendKind(action: CardAction): boolean {
  return action.actionKind === "gmail.send_reply" || action.actionKind === "gmail.forward";
}

/** A scheduled send: said with its time, and Undo cancels it (it can be cancelled until then). */
function scheduledUndo(queryClient: QueryClient, card: CardView, workId: string, at: string) {
  invalidateScheduled(queryClient);
  const when = sendAtWords(at, Date.now(), sendZoneFrom(queryClient));
  registerUndo({
    message: `Scheduled: sends ${when} · ${short(card.title)}`,
    undo: async () => {
      await cancelWork(queryClient, workId);
      invalidateScheduled(queryClient);
    },
    doneMessage: "Cancelled. Nothing will be sent; the card is back for review.",
    tooLateMessage: "Too late to cancel: it's already on its way.",
  });
}

/**
 * A reversible card action that already ran: its reverse change through the cardless thread route
 * (one more approval), then the card back to review. The card stays where it is if the executor
 * still holds the first change; the reverse runs after it either way.
 */
async function putBack(
  queryClient: QueryClient,
  cardId: string,
  threadKey: string,
  reverse: ThreadActionRequest,
): Promise<void> {
  try {
    await inboxApi.runAction(threadKey, reverse);
  } finally {
    invalidateThreadWork(queryClient);
  }
  await restoreCard(queryClient, cardId).catch(() => undefined);
}

export async function recoverWork(
  queryClient: QueryClient,
  work: Pick<WorkView, "id">,
  action: WorkRecovery,
): Promise<WorkView | null> {
  const done: Record<WorkRecovery, string> = {
    cancel: "Cancelled.",
    retry: "Queued to try again.",
    recover: "Recovered. It's back in the queue.",
    reconcile: "Checked Gmail. Showing what happened.",
    discard: "Dropped. The card is back for review; nothing was sent.",
  };
  try {
    const next = await feedApi.workAction(work.id, action);
    toast(done[action]);
    return next;
  } catch (error) {
    if (isApiError(error, "not_available") || (isApiError(error) && error.status === 404)) {
      toast("That isn't wired up yet. Nothing was changed.");
    } else if (isApiError(error) && error.status === 409) {
      toast(error.serverMessage ?? "This changed in the meantime. Showing the latest.");
    } else {
      toast("Couldn't do that just now. Try again.");
    }
    return null;
  } finally {
    invalidateBoards(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["work"] });
    void queryClient.invalidateQueries({ queryKey: ["card"] });
  }
}
